import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KeenIcon } from '@/components';
import { Container } from '@/components/container';
import { ConfirmationModal } from '@/components/modal';
import recordTypeService from '@/services/recordType/recordTypeService';
import customFieldService from '@/services/setting/customFieldService';
import { CustomFieldModal } from '../custom-fields/CustomFieldModal';

const PALETTE = '__palette';

const parseOptionsToArray = (options) => {
  if (!options) return [];
  try {
    return Array.isArray(options) ? options : JSON.parse(String(options).replace(/&quot;/g, '"'));
  } catch {
    return [];
  }
};

// ---- Draggable field card ----
const FieldCard = ({ field, onToggleWidth, onEdit, onRemove, onDelete, showRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 bg-white border rounded-md border-border ${
        field.width === 'full' ? 'sm:col-span-2' : ''
      }`}
    >
      <button
        type="button"
        className="text-gray-400 cursor-grab hover:text-gray-600"
        {...attributes}
        {...listeners}
      >
        <KeenIcon icon="dots-square" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">
          {field.title}
          {field.is_required ? <span className="pl-1 text-red-600">*</span> : null}
        </div>
        <div className="text-[11px] text-gray-500 capitalize">{field.field_type}</div>
      </div>
      <button
        type="button"
        title="Toggle width"
        className="btn btn-xs btn-light"
        onClick={() => onToggleWidth(field.id)}
      >
        {field.width === 'full' ? 'Full' : 'Half'}
      </button>
      <button type="button" title="Edit field" className="btn btn-xs btn-icon btn-light" onClick={() => onEdit(field)}>
        <KeenIcon icon="notepad-edit" />
      </button>
      {showRemove ? (
        <button
          type="button"
          title="Remove from section"
          className="btn btn-xs btn-icon btn-light"
          onClick={() => onRemove(field.id)}
        >
          <KeenIcon icon="exit-right-corner" />
        </button>
      ) : null}
      <button
        type="button"
        title="Delete field"
        className="btn btn-xs btn-icon btn-light"
        onClick={() => onDelete(field)}
      >
        <KeenIcon icon="trash" className="text-red-600" />
      </button>
    </div>
  );
};

// ---- Droppable field grid (section body or palette) ----
const FieldDropZone = ({ containerId, fieldIds, fieldsById, columns = 2, ...handlers }) => {
  const { setNodeRef, isOver } = useDroppable({ id: containerId });
  return (
    <SortableContext id={containerId} items={fieldIds} strategy={rectSortingStrategy}>
      <div
        ref={setNodeRef}
        className={`grid gap-2 min-h-[56px] rounded-md p-1 ${
          columns === 1 ? 'grid-cols-1' : 'sm:grid-cols-2'
        } ${isOver ? 'bg-primary/5 outline outline-1 outline-primary/30' : ''}`}
      >
        {fieldIds.length === 0 ? (
          <div className="flex items-center justify-center col-span-full text-[11px] text-gray-400 py-3">
            Drag fields here
          </div>
        ) : (
          fieldIds.map((fid) => (
            <FieldCard key={fid} field={fieldsById[fid]} {...handlers} />
          ))
        )}
      </div>
    </SortableContext>
  );
};

const RecordTypeLayoutEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [recordType, setRecordType] = useState(null);
  const [sections, setSections] = useState([]); // [{id,name,columns,fieldIds,isNew}]
  const [paletteIds, setPaletteIds] = useState([]);
  const [globalFields, setGlobalFields] = useState([]); // read-only until claimed
  const [fieldsById, setFieldsById] = useState({});
  const [deletedSectionUuids, setDeletedSectionUuids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newSectionSeq, setNewSectionSeq] = useState(1);

  // field modal
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [fieldModalMode, setFieldModalMode] = useState('create');
  const [fieldModalData, setFieldModalData] = useState(null);
  const [fieldModalLoading, setFieldModalLoading] = useState(false);
  const [claiming, setClaiming] = useState(false); // true when editing a global field to claim it

  // delete field confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  const hydrate = (layout) => {
    setRecordType(layout.record_type);
    const byId = {};
    const secs = (layout.sections || []).map((s) => {
      const fieldIds = (s.fields || []).map((f) => {
        byId[f.id] = f;
        return f.id;
      });
      return { id: s.id, name: s.name, columns: s.columns || 2, fieldIds, isNew: false };
    });
    (layout.unsectioned_fields || []).forEach((f) => {
      byId[f.id] = f;
    });
    setSections(secs);
    setPaletteIds((layout.unsectioned_fields || []).map((f) => f.id));
    setGlobalFields(layout.global_fields || []);
    setFieldsById(byId);
    setDeletedSectionUuids([]);
    setDirty(false);
  };

  const fetchLayout = async () => {
    setLoading(true);
    try {
      const { data, status } = await recordTypeService.getOne(id);
      if (status) hydrate(data);
    } catch {
      toast.error('Failed to load record type.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const containerIds = useMemo(() => [PALETTE, ...sections.map((s) => s.id)], [sections]);

  const findContainer = (itemId) => {
    if (itemId === PALETTE || containerIds.includes(itemId)) return itemId;
    if (paletteIds.includes(itemId)) return PALETTE;
    const sec = sections.find((s) => s.fieldIds.includes(itemId));
    return sec ? sec.id : null;
  };

  const getContainerItems = (containerId) =>
    containerId === PALETTE ? paletteIds : sections.find((s) => s.id === containerId)?.fieldIds || [];

  const setContainerItems = (containerId, items) => {
    if (containerId === PALETTE) {
      setPaletteIds(items);
    } else {
      setSections((prev) => prev.map((s) => (s.id === containerId ? { ...s, fieldIds: items } : s)));
    }
  };

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id);
    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    setDirty(true);
    const activeItems = getContainerItems(activeContainer).filter((i) => i !== active.id);
    const overItems = getContainerItems(overContainer);
    const overIndex = overItems.indexOf(over.id);
    const insertAt = overIndex >= 0 ? overIndex : overItems.length;
    const nextOver = [...overItems.slice(0, insertAt), active.id, ...overItems.slice(insertAt)];

    setContainerItems(activeContainer, activeItems);
    setContainerItems(overContainer, nextOver);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id);
    if (!activeContainer || !overContainer) return;

    if (activeContainer === overContainer) {
      const items = getContainerItems(activeContainer);
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      if (oldIndex !== newIndex && newIndex >= 0) {
        setContainerItems(activeContainer, arrayMove(items, oldIndex, newIndex));
        setDirty(true);
      }
    }
  };

  // ---- Section operations ----
  const addSection = () => {
    setSections((prev) => [
      ...prev,
      { id: `new-${newSectionSeq}`, name: 'New Section', columns: 2, fieldIds: [], isNew: true },
    ]);
    setNewSectionSeq((n) => n + 1);
    setDirty(true);
  };

  const renameSection = (sectionId, name) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, name } : s)));
    setDirty(true);
  };

  const setSectionColumns = (sectionId, columns) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, columns } : s)));
    setDirty(true);
  };

  const moveSection = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= sections.length) return;
    setSections((prev) => arrayMove(prev, index, target));
    setDirty(true);
  };

  const deleteSection = (section) => {
    // Move its fields back to the palette (never delete the fields here).
    setPaletteIds((prev) => [...prev, ...section.fieldIds]);
    setSections((prev) => prev.filter((s) => s.id !== section.id));
    if (!section.isNew) setDeletedSectionUuids((prev) => [...prev, section.id]);
    setDirty(true);
  };

  // ---- Field operations ----
  const toggleFieldWidth = (fieldId) => {
    setFieldsById((prev) => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], width: prev[fieldId].width === 'full' ? 'half' : 'full' },
    }));
    setDirty(true);
  };

  const removeFieldToPalette = (fieldId) => {
    const container = findContainer(fieldId);
    if (container === PALETTE || !container) return;
    setContainerItems(
      container,
      getContainerItems(container).filter((i) => i !== fieldId)
    );
    setPaletteIds((prev) => [...prev, fieldId]);
    setDirty(true);
  };

  const openNewField = () => {
    setFieldModalMode('create');
    setFieldModalData(null);
    setClaiming(false);
    setFieldModalOpen(true);
  };

  const openEditField = (field) => {
    setFieldModalMode('edit');
    setFieldModalData({ ...field, options: parseOptionsToArray(field.options) });
    setClaiming(false);
    setFieldModalOpen(true);
  };

  const openClaimGlobal = (field) => {
    setFieldModalMode('edit');
    setFieldModalData({ ...field, options: parseOptionsToArray(field.options) });
    setClaiming(true);
    setFieldModalOpen(true);
  };

  const handleFieldSubmit = async (payload, helpers) => {
    setFieldModalLoading(true);
    try {
      if (fieldModalMode === 'edit' && fieldModalData) {
        const { data, status } = await customFieldService.updateCustomField(payload, fieldModalData.id);
        if (status) {
          setFieldsById((prev) => ({ ...prev, [data.id]: data }));
          if (claiming) {
            // Claimed a global field: move it into this type's palette.
            setGlobalFields((prev) => prev.filter((g) => g.id !== data.id));
            setPaletteIds((prev) => (prev.includes(data.id) ? prev : [...prev, data.id]));
          }
          toast.success('Field updated.');
        }
      } else {
        const { data, status } = await customFieldService.createCustomField(payload);
        if (status) {
          setFieldsById((prev) => ({ ...prev, [data.id]: data }));
          setPaletteIds((prev) => [...prev, data.id]);
          toast.success('Field created.');
        }
      }
      setFieldModalOpen(false);
      helpers?.resetForm?.();
      setDirty(true);
    } catch (error) {
      if (error?.errors) helpers?.setErrors?.(error.errors);
      toast.error(error?.message || 'Failed to save field.');
    } finally {
      setFieldModalLoading(false);
    }
  };

  const confirmDeleteField = async () => {
    const field = deleteTarget;
    setDeleteTarget(null);
    if (!field) return;
    try {
      const { status } = await customFieldService.deleteCustomField(field.id);
      if (status) {
        const container = findContainer(field.id);
        if (container) {
          setContainerItems(container, getContainerItems(container).filter((i) => i !== field.id));
        }
        setFieldsById((prev) => {
          const next = { ...prev };
          delete next[field.id];
          return next;
        });
        toast.success('Field deleted.');
      }
    } catch (error) {
      toast.warning(error?.message || 'This field is in use and cannot be deleted.');
    }
  };

  // ---- Save ----
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        sections: sections.map((s, i) => ({
          uuid: s.isNew ? null : s.id,
          name: s.name,
          columns: s.columns,
          sort_order: i,
          fields: s.fieldIds.map((fid, idx) => ({
            uuid: fid,
            sort_order: idx,
            width: fieldsById[fid]?.width || 'half',
          })),
        })),
        deleted_section_uuids: deletedSectionUuids,
      };
      const { data, status } = await recordTypeService.saveLayout(id, payload);
      if (status) {
        hydrate(data);
        toast.success('Layout saved.');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to save layout.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="py-16 text-sm text-center text-gray-500">Loading…</div>
      </Container>
    );
  }

  const fieldHandlers = {
    fieldsById,
    onToggleWidth: toggleFieldWidth,
    onEdit: openEditField,
    onRemove: removeFieldToPalette,
    onDelete: (field) => setDeleteTarget(field),
    showRemove: true,
  };

  return (
    <Container>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button className="btn btn-sm btn-light" onClick={() => navigate('/account/home/record-types')}>
            <KeenIcon icon="arrow-left" /> Back
          </button>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full"
            style={{
              backgroundColor: `${recordType?.color || '#6B7280'}1A`,
              color: recordType?.color || '#6B7280',
            }}
          >
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: recordType?.color || '#6B7280' }} />
            {recordType?.name}
          </span>
          <span className="text-sm text-gray-500">Layout</span>
        </div>
        <div className="flex items-center gap-2">
          {dirty ? <span className="text-xs text-amber-600">Unsaved changes</span> : null}
          <button className="btn btn-sm btn-light" onClick={fetchLayout} disabled={saving}>
            Discard
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Layout'}
          </button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          {/* Canvas */}
          <div className="space-y-4">
            {/* Fixed core-fields placeholder */}
            <div className="p-4 border border-dashed rounded-lg border-border bg-gray-50">
              <div className="text-sm font-medium text-gray-700">Matter Details (core fields)</div>
              <div className="mt-1 text-xs text-gray-500">
                Name, Contact, Assigned To, Matter Id, Dates, Status, Tags, Note — always shown at the top.
              </div>
            </div>

            {sections.map((section, index) => (
              <div key={section.id} className="border rounded-lg card border-border">
                <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      className="leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      onClick={() => moveSection(index, -1)}
                      disabled={index === 0}
                    >
                      <KeenIcon icon="up" />
                    </button>
                    <button
                      type="button"
                      className="leading-none text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      onClick={() => moveSection(index, 1)}
                      disabled={index === sections.length - 1}
                    >
                      <KeenIcon icon="down" />
                    </button>
                  </div>
                  <input
                    className="flex-1 min-w-[160px] input input-sm"
                    value={section.name}
                    onChange={(e) => renameSection(section.id, e.target.value)}
                  />
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className={`btn btn-xs ${section.columns === 1 ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setSectionColumns(section.id, 1)}
                    >
                      1 col
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs ${section.columns === 2 ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setSectionColumns(section.id, 2)}
                    >
                      2 col
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn btn-xs btn-icon btn-light"
                    title="Delete section"
                    onClick={() => deleteSection(section)}
                  >
                    <KeenIcon icon="trash" className="text-red-600" />
                  </button>
                </div>
                <div className="p-3">
                  <FieldDropZone
                    containerId={section.id}
                    fieldIds={section.fieldIds}
                    columns={section.columns}
                    {...fieldHandlers}
                  />
                </div>
              </div>
            ))}

            <button className="btn btn-sm btn-light btn-outline" onClick={addSection}>
              <KeenIcon icon="plus" /> Add Section
            </button>
          </div>

          {/* Palette */}
          <div className="space-y-4">
            <div className="border rounded-lg card border-border">
              <div className="flex items-center justify-between p-3 border-b border-border">
                <span className="text-sm font-medium">Field Palette</span>
                <button className="btn btn-xs btn-primary" onClick={openNewField}>
                  <KeenIcon icon="plus" /> New Field
                </button>
              </div>
              <div className="p-3">
                <p className="mb-2 text-xs text-gray-500">Unsectioned fields — drag into a section.</p>
                <FieldDropZone
                  containerId={PALETTE}
                  fieldIds={paletteIds}
                  columns={1}
                  {...fieldHandlers}
                  showRemove={false}
                />
              </div>
            </div>

            {globalFields.length ? (
              <div className="border rounded-lg card border-border">
                <div className="p-3 border-b border-border">
                  <span className="text-sm font-medium">Global Matter Fields</span>
                  <p className="mt-1 text-[11px] text-gray-500">
                    Shown on every matter. Add one to this type to make it type-specific (removes it from other types).
                  </p>
                </div>
                <div className="p-3 space-y-2">
                  {globalFields.map((gf) => (
                    <div key={gf.id} className="flex items-center gap-2 p-2 border rounded-md border-border">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{gf.title}</div>
                        <div className="text-[11px] text-gray-500 capitalize">{gf.field_type}</div>
                      </div>
                      <button className="btn btn-xs btn-light" onClick={() => openClaimGlobal(gf)}>
                        Add to type
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </DndContext>

      <CustomFieldModal
        open={fieldModalOpen}
        onClose={() => setFieldModalOpen(false)}
        mode={fieldModalMode}
        data={fieldModalData}
        onSubmit={handleFieldSubmit}
        createFieldLoading={fieldModalLoading}
        updateFieldLoading={fieldModalLoading}
        moduleType="matter"
        recordTypeId={id}
      />

      <ConfirmationModal
        show={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteField}
        btnText="Delete"
        message="Delete this field permanently? This removes it from the record type."
        title="Delete Field?"
      />
    </Container>
  );
};

export { RecordTypeLayoutEditor };
